<script setup lang="ts">
import { NButton, NInput, NSelect } from 'naive-ui'
import type { ListItemInput } from '../stores/lists'

// The add-item row at the foot of the selected list: a title input and an
// Add button, with the optional URL / notes / priority tucked under a
// disclosure (open while editing an item). The parent owns the form object
// and decides whether a submit adds or saves an edit.
const props = defineProps<{
  form: ListItemInput
  listLabel: string
  editing: boolean
  saving: boolean
}>()

const emit = defineEmits<{ submit: []; cancel: [] }>()

// Bound rather than written in the template: vue-tsc reads the `//` of a
// literal URL in a component's props as a comment and loses the scope.
const URL_PLACEHOLDER = 'https://…'

const PRIORITY_OPTIONS = [
  { label: 'High', value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'Low', value: 3 },
]

const LABEL_CLASS = 'flex flex-col gap-1 text-[13px] text-ink-muted'
</script>

<template>
  <div class="flex flex-col gap-2 pt-2.5" data-testid="list-item-composer">
    <p v-if="editing" class="m-0 text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-faint">Edit item</p>
    <div class="flex gap-2">
      <NInput
        v-model:value="props.form.title"
        class="min-w-0 flex-1"
        size="large"
        :placeholder="`${listLabel} item…`"
        :input-props="{ 'aria-label': editing ? 'Item title' : `Add an item to ${listLabel}` }"
        @keydown.enter.prevent="emit('submit')"
      />
      <NButton
        class="max-sm:min-h-11"
        size="large"
        :type="editing ? 'primary' : 'default'"
        :loading="saving"
        @click="emit('submit')"
      >
        <template v-if="editing">Save changes</template>
        <template v-else
          >Add<span class="sr-only"> to {{ listLabel }}</span></template
        >
      </NButton>
      <NButton v-if="editing" size="large" quaternary @click="emit('cancel')">Cancel</NButton>
    </div>
    <details class="group text-[13px]" :open="editing">
      <summary class="inline-flex min-h-11 cursor-pointer items-center text-ink-muted hover:text-ink sm:min-h-8">
        Link, notes and priority
      </summary>
      <div class="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3 pt-2">
        <label :class="LABEL_CLASS">
          URL (optional)
          <NInput v-model:value="props.form.url" :placeholder="URL_PLACEHOLDER" />
        </label>
        <label :class="LABEL_CLASS">
          Priority (optional)
          <NSelect
            v-model:value="props.form.priority"
            :options="PRIORITY_OPTIONS"
            clearable
            placeholder="Unprioritized"
          />
        </label>
        <label :class="[LABEL_CLASS, 'col-span-full']">
          Notes (optional)
          <NInput v-model:value="props.form.notes" type="textarea" :rows="2" placeholder="Any context…" />
        </label>
      </div>
    </details>
  </div>
</template>
